/**
 * Pieces shared by the food page's "Add New Serving" form (FoodServingsCard)
 * and the recipe draft's "Add serving" dialog (RecipeServingsTable), so the two
 * forms generate the same description and say the same things.
 */

// The serving's display name: a fixed "unit (~portion)" format that also shows in
// recipes and meal plans. The portion is the typed weight, labelled g or ml. The
// label only: nothing is converted, so "ml" means the same number is shown as ml.
// A unit name should be the generic measure (Cup, Glass); the portion carries the
// size, so a unit that already says "(200 ml)" repeats it.
export const buildDescription = (
  unitName: string | undefined,
  weight: string,
  unit: "g" | "ml" = "g"
) => {
  if (!unitName) return "";
  const trimmed = weight?.trim();
  if (!trimmed) return unitName;
  const num = parseFloat(trimmed);
  if (Number.isNaN(num)) return unitName;
  return unit === "ml" ? `${unitName} (~${num} ml)` : `${unitName} (~${num}g)`;
};

export const SERVING_DESCRIPTION_PLACEHOLDER =
  "Auto-generated from serving unit and weight";
export const SERVING_DESCRIPTION_HELPER =
  "Auto-generated from Serving Unit and Weight (g).";
export const SERVING_ML_WEIGHT_HELPER =
  "Still the weight in grams. The name shows this number as ml and does not convert it.";
export const SERVING_MULTIPLIER_PLACEHOLDER =
  "Enter a weight to see the multiplier";
export const SERVING_MULTIPLIER_HELPER =
  "Set by the server as Weight (g) ÷ 100, because nutrient values are stored per 100 g. A 290 g slice is 2.9.";

/** Weight ÷ 100 to four places, as the server computes it. "" when unusable. */
export const previewMultiplierFor = (weight: string): string => {
  const w = parseFloat(weight);
  return Number.isFinite(w) && w > 0 ? (w / 100).toFixed(4) : "";
};

/** Reads the g/ml label back out of a saved description. */
export const nameUnitFromDescription = (
  description: string | undefined
): "g" | "ml" => (description?.trim().endsWith(" ml)") ? "ml" : "g");
